import React, { useRef, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { toastError, toastSuccess } from '../ui/toast';
import { useUserRole } from '../hooks/useUserRole';
import CreateUserModal from './CreateUserModal';
import {
  logAuditAction, logUserApproval, logUserSuspension, logUserReactivation,
  logRoleChange, logCoachAssignment, logUserArchive, logUserRestore,
  AUDIT_ACTIONS, RESOURCE_TYPES,
} from '../utils/auditLog';
import { getLifecycleBucket, getInactiveReason, LIFECYCLE_TABS } from '../utils/lifecycle';
import { colors as theme } from '../theme';

const c = {
  bg: theme.bg, card: theme.surface1, card2: '#0F1530', border: theme.surface3,
  text: theme.text, muted: theme.textMuted, dimmed: '#5A6280',
  gold: theme.gold, green: '#4A8F62', purple: '#8B72D0',
  error: theme.danger, warning: theme.warning, success: theme.success,
};

const TYPE_META = {
  bundle:         { label: 'Bundle',    color: '#8B72D0' },
  fitness_only:   { label: 'Fitness',   color: theme.gold },
  nutrition_only: { label: 'Nutrition', color: '#4A8F62' },
  staff:          { label: 'Staff',     color: theme.textMuted },
  none:           { label: 'No Plan',   color: '#5A6280' },
};

const STATUS_META = {
  active:    { label: 'Active',    color: theme.success },
  pending:   { label: 'Pending',   color: theme.warning },
  suspended: { label: 'Suspended', color: theme.danger },
  rejected:  { label: 'Rejected',  color: theme.danger },
};

const TIER_META = {
  free:    { label: 'Free',    color: '#5A6280' },
  monthly: { label: 'Monthly', color: '#4A8F62' },
  annual:  { label: 'Annual',  color: '#4A8F62' },
  bundle:  { label: 'Bundle',  color: '#8B72D0' },
};

const timeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const days = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const activityColor = (dateStr) => {
  if (!dateStr) return c.dimmed;
  const days = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (days <= 3) return c.success;
  if (days <= 7) return c.warning;
  return c.error;
};

function Badge({ label, color }) {
  return (
    <span style={{
      padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      color, background: `${color}18`, border: `1px solid ${color}35`,
      textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function ActionBtn({ label, color = c.gold, onClick, disabled, small }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '5px 12px' : '8px 16px',
      borderRadius: 8, fontSize: small ? 11 : 13, fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
      background: `${color}15`, border: `1px solid ${color}45`, color,
      fontFamily: 'inherit', transition: 'all 0.15s',
    }}>{label}</button>
  );
}

function Field({ label, value, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${c.border}` }}>
      <span style={{ color: c.muted, fontSize: 13 }}>{label}</span>
      <span style={{ color: muted ? c.muted : c.text, fontSize: 13, fontWeight: 500 }}>{value ?? '—'}</span>
    </div>
  );
}

export default function PeoplePanel({ onBack, onViewClient }) {
  const { isSuperAdmin, isAdmin, role, user: currentUser } = useUserRole();
  const [people, setPeople]       = useState([]);
  const [coaches, setCoaches]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('members');
  const [selected, setSelected]   = useState(null);
  const [activeTab, setActiveTab] = useState('fitness');
  const [acting, setActing]             = useState(null);
  const [compUntil, setCompUntil]       = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nutritionProfile, setNutritionProfile] = useState(null);
  const [nutritionLoading, setNutritionLoading] = useState(false);
  const [showCreateCoach, setShowCreateCoach] = useState(false);

  useEffect(() => {
    setCompUntil('');
    setActiveTab('fitness');
    setConfirmDelete(false);
    setNutritionProfile(null);
  }, [selected?.auth_user_id]);

  // ── API helpers ────────────────────────────────────────────────────────────
  const getAuthHeader = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session ? `Bearer ${session.access_token}` : null;
  };

  const callApi = async (body) => {
    const auth = await getAuthHeader();
    const res = await fetch('/api/nutrition-admin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: auth },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  };

  // ── Load data ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [peopleData, { data: coachRows }] = await Promise.all([
        callApi({ action: 'list_people' }),
        supabase.from('users').select('id, auth_user_id, name, email, role').in('role', ['coach', 'admin', 'super_admin']).eq('status', 'active'),
      ]);
      setPeople(peopleData.people ?? []);
      setCoaches(coachRows ?? []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only the latest request may fill the panel, and the last person's answers are cleared first, so one
  // person's health answers never show under another's name (Codex's review of migration 083, S4)
  const nutritionFor = useRef(null);
  const loadNutritionProfile = useCallback(async (authUserId) => {
    if (!authUserId) return;
    nutritionFor.current = authUserId;
    setNutritionProfile(null);
    setNutritionLoading(true);
    try {
      const data = await callApi({ action: 'get_nutrition_profile', auth_user_id: authUserId });
      if (nutritionFor.current === authUserId) setNutritionProfile(data);
    } catch (err) {
      if (nutritionFor.current === authUserId) setNutritionProfile(null);
    } finally {
      if (nutritionFor.current === authUserId) setNutritionLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (id, updates) => {
    setPeople(prev => prev.map(p => p.auth_user_id === id ? { ...p, ...updates } : p));
    setSelected(prev => prev?.auth_user_id === id ? { ...prev, ...updates } : prev);
  };

  // ── Fitness actions ────────────────────────────────────────────────────────
  const handleApprove = async (person) => {
    setActing('approve');
    const wasReactivation = person.fitness_status === 'suspended';
    try {
      const { error } = await supabase.from('users').update({ status: 'active', approved_at: new Date().toISOString() }).eq('id', person.fitness_id);
      if (error) throw error;
      patch(person.auth_user_id, { fitness_status: 'active' });
      if (wasReactivation) {
        logUserReactivation(person.fitness_id, person.name ?? person.email);
      } else {
        logUserApproval(person.fitness_id, person.name ?? person.email);
      }
      toastSuccess(`${person.name ?? person.email} approved`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleSuspend = async (person) => {
    setActing('suspend');
    try {
      const { error } = await supabase.from('users').update({ status: 'suspended' }).eq('id', person.fitness_id);
      if (error) throw error;
      patch(person.auth_user_id, { fitness_status: 'suspended' });
      logUserSuspension(person.fitness_id, person.name ?? person.email);
      toastSuccess(`${person.name ?? person.email} suspended`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleRoleChange = async (person, newRole) => {
    setActing('role');
    try {
      const { error } = await supabase.from('users').update({ role: newRole }).eq('id', person.fitness_id);
      if (error) throw error;
      patch(person.auth_user_id, { fitness_role: newRole });
      logRoleChange(person.fitness_id, person.name ?? person.email, person.fitness_role, newRole);
      toastSuccess('Role updated');
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleCoachChange = async (person, coachId) => {
    setActing('coach');
    try {
      const { error } = await supabase.from('users').update({ coach_id: coachId || null }).eq('id', person.fitness_id);
      if (error) throw error;
      const coach = coaches.find(c => c.auth_user_id === coachId);
      patch(person.auth_user_id, { coach_id: coachId, coach_name: coach?.name ?? null });
      logCoachAssignment(person.fitness_id, person.name ?? person.email, coachId, coach?.name ?? null);
      toastSuccess('Coach updated');
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleMarkReviewed = async (person) => {
    setActing('review');
    try {
      const { error } = await supabase.from('users').update({ needs_review: false }).eq('id', person.fitness_id);
      if (error) throw error;
      patch(person.auth_user_id, { needs_review: false });
      toastSuccess('Marked as reviewed');
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  // Complimentary time changes only the app: Stripe keeps charging a trial or a paying client. So the owner is
  // warned first and their Stripe page is opened, to extend the trial or pause payments there (owner,
  // 26 October 2026, choice A; api/complimentary-billing.js only reads Stripe).
  const stripeBilling = async (person) => {
    const res = await fetch('/api/complimentary-billing', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: await getAuthHeader() },
      body: JSON.stringify({ auth_user_id: person.auth_user_id }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error || 'Could not check Stripe. Try again.');
    return body;
  };
  // A write that matched no row reports no error: the row must come back (src/utils/dbWrite.js)
  const saveComplimentary = async (person, fields) => {
    const { data, error } = await supabase.from('users').update(fields).eq('id', person.fitness_id).select('id');
    if (error) throw error;
    if (!data?.length) throw new Error('Nothing was saved: the account was not found or could not be changed.');
  };

  const handleGrantComplimentary = async (person) => {
    if (!compUntil) return toastError('Select an expiry date');
    setActing('comp');
    try {
      const billing = await stripeBilling(person);
      if (billing.state !== 'none'
          && !window.confirm(`${billing.message}\n\nGive complimentary time now and open their Stripe page?`)) return;
      await saveComplimentary(person, { is_complimentary: true, complimentary_until: compUntil });
      patch(person.auth_user_id, { is_complimentary: true, complimentary_until: compUntil });
      logAuditAction(
        AUDIT_ACTIONS.GRANT_COMPLIMENTARY,
        RESOURCE_TYPES.USER,
        person.fitness_id,
        { is_complimentary: false },
        { is_complimentary: true, complimentary_until: compUntil, userName: person.name ?? person.email }
      );
      toastSuccess(`Complimentary granted until ${new Date(compUntil).toLocaleDateString('en-GB')}`);
      if (billing.link) window.open(billing.link, '_blank', 'noopener');
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleRevokeComplimentary = async (person) => {
    setActing('comp');
    try {
      // A trial extended or payments paused by hand in Stripe stay so until changed there too
      let billing = { state: 'none' };
      try { billing = await stripeBilling(person); } catch { /* the reminder is a help, not a condition */ }
      await saveComplimentary(person, { is_complimentary: false, complimentary_until: null });
      patch(person.auth_user_id, { is_complimentary: false, complimentary_until: null });
      logAuditAction(
        AUDIT_ACTIONS.REVOKE_COMPLIMENTARY,
        RESOURCE_TYPES.USER,
        person.fitness_id,
        { is_complimentary: true },
        { is_complimentary: false, userName: person.name ?? person.email }
      );

      // Complimentary access sometimes carries a silent bundle grant on the
      // Nutrition side (switch-to-nutrition.js auto-grants it if they ever
      // switched apps while complimentary). Clear that too so this button
      // actually revokes everything it gave them for free — never touches a
      // real paying subscription (server checks for a Stripe subscription id).
      let bundleCleared = false;
      try {
        const result = await callApi({ action: 'revoke_if_no_stripe', email: person.email });
        bundleCleared = !!result?.revoked;
        if (bundleCleared) patch(person.auth_user_id, { nutrition_tier: 'free' });
      } catch (cleanupErr) {
        console.warn('[revoke complimentary] bundle cleanup failed (non-fatal):', cleanupErr.message);
      }

      toastSuccess(bundleCleared ? 'Complimentary revoked (bundle access cleared too)' : 'Complimentary revoked');
      if (billing.link && window.confirm('If you extended their trial or paused their payments in Stripe, change it back there. Open their Stripe page?')) {
        window.open(billing.link, '_blank', 'noopener');
      }
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleAssignProgram = async (person) => {
    setActing('assign');
    try {
      const auth = await getAuthHeader();
      const res = await fetch('/api/match-program', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: auth },
        body: JSON.stringify({ target_auth_user_id: person.auth_user_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      patch(person.auth_user_id, { has_program: true, needs_review: true });
      toastSuccess(`Program assigned: ${data.matched}`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  // ── Nutrition actions ──────────────────────────────────────────────────────
  const handleGrantNutrition = async (person, tier) => {
    setActing('nutrition');
    try {
      await callApi({ action: 'grant', email: person.email, tier });
      patch(person.auth_user_id, { nutrition_tier: tier, nutrition_status: 'active' });
      toastSuccess(`Nutrition ${tier} granted`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleDeleteUser = async (person) => {
    setActing('delete');
    try {
      await callApi({ action: 'delete_user', auth_user_id: person.auth_user_id });
      setPeople(prev => prev.filter(p => p.auth_user_id !== person.auth_user_id));
      setSelected(null);
      setConfirmDelete(false);
      toastSuccess(`${person.name ?? person.email} deleted permanently`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleRevokeNutrition = async (person) => {
    setActing('nutrition');
    try {
      await callApi({ action: 'revoke', email: person.email });
      patch(person.auth_user_id, { nutrition_tier: 'free', nutrition_status: null });
      toastSuccess('Nutrition access revoked');
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  // ── Archive / restore ──────────────────────────────────────────────────────
  const handleArchive = async (person, reason = 'manual') => {
    setActing('archive');
    try {
      const { data, error } = await supabase.rpc('set_client_archived', {
        target_auth_user_id: person.auth_user_id,
        archived: true,
        reason,
      });
      if (error) throw error;
      patch(person.auth_user_id, {
        archived_at: data ?? new Date().toISOString(),
        archive_reason: reason,
      });
      logUserArchive(person.fitness_id, person.name ?? person.email, reason);
      toastSuccess(`${person.name ?? person.email} moved to Inactive`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  const handleRestore = async (person) => {
    setActing('archive');
    try {
      const { error } = await supabase.rpc('set_client_archived', {
        target_auth_user_id: person.auth_user_id,
        archived: false,
      });
      if (error) throw error;
      patch(person.auth_user_id, { archived_at: null, archive_reason: null });
      logUserRestore(person.fitness_id, person.name ?? person.email);
      toastSuccess(`${person.name ?? person.email} restored`);
    } catch (err) { toastError(err.message); } finally { setActing(null); }
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  // Normalize a person into the shape getLifecycleBucket() expects (it keys on
  // role/status, PeoplePanel carries them as fitness_role/fitness_status).
  const bucketOf = (p) => getLifecycleBucket({ ...p, role: p.fitness_role, status: p.fitness_status });

  const counts = { members: 0, needs_action: 0, staff: 0, inactive: 0, all: people.length };
  for (const p of people) counts[bucketOf(p)]++;

  const TABS = LIFECYCLE_TABS.map(t => ({
    ...t,
    count: t.key === 'all' ? counts.all : counts[t.key],
  }));

  const filtered = people.filter(p => {
    if (search) {
      const s = search.toLowerCase();
      if (!p.email?.toLowerCase().includes(s) && !(p.name ?? '').toLowerCase().includes(s)) return false;
    }
    if (filter === 'all') return true;
    return bucketOf(p) === filter;
  });

  // ── Detail panel ───────────────────────────────────────────────────────────
  const renderFitnessTab = (p) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Status */}
      <Section title="Status & Access">
        <Field label="Status" value={
          <Badge label={STATUS_META[p.fitness_status]?.label ?? p.fitness_status} color={STATUS_META[p.fitness_status]?.color ?? c.muted} />
        } />
        <Field label="Program" value={
          p.has_program
            ? <Badge label="Assigned" color={c.green} />
            : <Badge label="No program" color={c.error} />
        } />
        <Field label="Subscription" value={
          p.nutrition_tier === 'bundle'
            ? `Bundle — ${p.nutrition_status ?? 'unknown'}${p.nutrition_period_end ? ` (renews ${new Date(p.nutrition_period_end).toLocaleDateString()})` : ''}`
            : p.subscription_status ?? 'None'
        } muted={p.nutrition_tier !== 'bundle' && !p.subscription_status} />
        <Field label="Access type" value={
          p.archived_at ? 'Inactive (archived)' :
          p.is_complimentary ? 'Complimentary' :
          p.nutrition_tier === 'bundle' ? 'Bundle (Nutrition + Fitness)' :
          p.subscription_status === 'active' ? 'Fitness (Paid)' :
          p.subscription_status === 'trialing' ? 'Fitness (Trial)' :
          p.subscription_status === 'past_due' ? 'Past Due' :
          p.subscription_status === 'canceled' ? 'Canceled' :
          'No Access'
        } />
        {p.archived_at && (
          <Field label="Archived" value={
            `${new Date(p.archived_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}${p.archive_reason ? ` · ${p.archive_reason.replace(/_/g, ' ')}` : ''}`
          } muted />
        )}
        <div style={{ display: 'flex', gap: 8, paddingTop: 10, flexWrap: 'wrap' }}>
          {p.archived_at
            ? <ActionBtn label={acting === 'archive' ? '…' : 'Restore to Members'} color={c.gold} onClick={() => handleRestore(p)} disabled={!!acting} />
            : <ActionBtn label={acting === 'archive' ? '…' : 'Archive (move to Inactive)'} color={c.muted} onClick={() => handleArchive(p, 'manual')} disabled={!!acting} />}
          {p.fitness_status === 'active'  && <ActionBtn label={acting === 'suspend' ? '…' : 'Suspend'} color={c.error} onClick={() => handleSuspend(p)} disabled={!!acting} />}
          {p.fitness_status === 'suspended' && <ActionBtn label={acting === 'approve' ? '…' : 'Reactivate'} color={c.gold} onClick={() => handleApprove(p)} disabled={!!acting} />}
          {p.needs_review && <ActionBtn label={acting === 'review' ? '…' : 'Mark Reviewed'} color={c.warning} onClick={() => handleMarkReviewed(p)} disabled={!!acting} />}
          {p.fitness_role === 'client' && !p.has_program && (
            <ActionBtn label={acting === 'assign' ? 'Assigning…' : 'Assign Program'} color={c.purple} onClick={() => handleAssignProgram(p)} disabled={!!acting} />
          )}
          {p.fitness_role === 'client' && onViewClient && (
            <ActionBtn label="View Program →" color={c.gold} onClick={() => { setSelected(null); onViewClient(p.fitness_id); }} />
          )}
        </div>
      </Section>

      {/* Role & Coach */}
      <Section title="Role & Assignment">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, color: c.muted, display: 'block', marginBottom: 6 }}>Role</label>
            <select
              value={p.fitness_role ?? ''}
              onChange={e => handleRoleChange(p, e.target.value)}
              disabled={!!acting || (!isSuperAdmin && !isAdmin)}
              style={selectStyle}
            >
              <option value="client">Client</option>
              <option value="coach">Coach</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: c.muted, display: 'block', marginBottom: 6 }}>Assigned Coach</label>
            <select
              value={p.coach_id ?? ''}
              onChange={e => handleCoachChange(p, e.target.value)}
              disabled={!!acting}
              style={selectStyle}
            >
              <option value="">— Unassigned —</option>
              {coaches.filter(coach => coach.id !== p.fitness_id).map(coach => (
                <option key={coach.auth_user_id} value={coach.auth_user_id}>{coach.name ?? coach.email} ({coach.role})</option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      {/* Danger zone */}
      {isSuperAdmin && (
        <Section title="Danger Zone">
          {!confirmDelete ? (
            <div style={{ padding: '4px 0' }}>
              <ActionBtn label="Delete Account" color={c.error} onClick={() => setConfirmDelete(true)} />
            </div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              <p style={{ color: c.error, fontSize: 13, margin: '0 0 12px', fontWeight: 600 }}>
                This permanently deletes their account, fitness profile, nutrition data, and all records. Cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <ActionBtn label={acting === 'delete' ? 'Deleting…' : 'Yes, delete permanently'} color={c.error} onClick={() => handleDeleteUser(selected)} disabled={!!acting} />
                <ActionBtn label="Cancel" color={c.muted} onClick={() => setConfirmDelete(false)} disabled={!!acting} />
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Complimentary — super_admin only; coaches must never be able to comp access */}
      {isSuperAdmin && (
        <Section title="Complimentary Access">
          {p.is_complimentary ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label="Expires" value={p.complimentary_until ? new Date(p.complimentary_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No expiry'} />
              <ActionBtn label={acting === 'comp' ? '…' : 'Revoke Complimentary'} color={c.error} onClick={() => handleRevokeComplimentary(p)} disabled={!!acting} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ color: c.muted, fontSize: 13, margin: 0 }}>No complimentary access active.</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ fontSize: 12, color: c.muted, display: 'block', marginBottom: 4 }}>Expiry date</label>
                  <input
                    type="date"
                    value={compUntil}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => setCompUntil(e.target.value)}
                    style={{ ...selectStyle, width: 'auto' }}
                  />
                </div>
                <div style={{ paddingTop: 18 }}>
                  <ActionBtn label={acting === 'comp' ? '…' : 'Grant Complimentary'} color={c.gold} onClick={() => handleGrantComplimentary(p)} disabled={!!acting || !compUntil} />
                </div>
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  );

  const renderNutritionTab = (p) => {
    const np = nutritionProfile;
    const fmt = (val) => val ? String(val).replace(/_/g, ' ') : '—';
    const fmtList = (arr) => arr?.length ? arr.map(s => s.replace(/_/g, ' ')).join(', ') : '—';
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Subscription */}
        <Section title="Subscription">
          <Field label="Tier" value={<Badge label={TIER_META[p.nutrition_tier]?.label ?? p.nutrition_tier ?? 'None'} color={TIER_META[p.nutrition_tier]?.color ?? c.muted} />} />
          <Field label="Status" value={fmt(p.nutrition_status)} muted={!p.nutrition_status} />
          <Field label="Renews" value={fmtDate(p.nutrition_period_end)} muted />
          <div style={{ display: 'flex', gap: 8, paddingTop: 10, flexWrap: 'wrap' }}>
            {p.nutrition_tier !== 'monthly' && <ActionBtn label={acting === 'nutrition' ? '…' : 'Grant Monthly'} color={c.green} onClick={() => handleGrantNutrition(p, 'monthly')} disabled={!!acting} />}
            {p.nutrition_tier !== 'bundle'  && <ActionBtn label={acting === 'nutrition' ? '…' : 'Grant Bundle'}  color={c.purple} onClick={() => handleGrantNutrition(p, 'bundle')}  disabled={!!acting} />}
            {p.nutrition_tier && p.nutrition_tier !== 'free' && <ActionBtn label={acting === 'nutrition' ? '…' : 'Revoke Access'} color={c.error} onClick={() => handleRevokeNutrition(p)} disabled={!!acting} />}
          </div>
        </Section>

        {/* Onboarding profile */}
        {nutritionLoading && <div style={{ color: c.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Loading profile…</div>}

        {!nutritionLoading && !np?.profile && (
          <div style={{ color: c.muted, fontSize: 13 }}>No nutrition onboarding data found for this person.</div>
        )}

        {!nutritionLoading && np?.profile && (
          <>
            <Section title="Personal">
              <Field label="Name"       value={np.profile.first_name} />
              <Field label="DOB"        value={fmtDate(np.profile.date_of_birth)} muted />
              <Field label="Sex"        value={fmt(np.profile.sex)} muted />
              <Field label="Height"     value={np.profile.height_cm ? `${np.profile.height_cm} cm` : '—'} muted />
              <Field label="Weight"     value={np.profile.current_weight_kg ? `${np.profile.current_weight_kg} kg` : '—'} muted />
              <Field label="Goal wt"    value={np.profile.goal_weight_kg ? `${np.profile.goal_weight_kg} kg` : '—'} muted />
              <Field label="Onboarded"  value={np.profile.onboarding_complete ? 'Yes' : 'No'} muted />
            </Section>

            <Section title="Goals & Activity">
              <Field label="Goal type"     value={fmt(np.profile.goal_type)} />
              <Field label="Activity lvl"  value={fmt(np.profile.activity_level)} muted />
              <Field label="Meals/day"     value={np.profile.meals_per_day ?? '—'} muted />
            </Section>

            {np.dietary && (
              <Section title="Dietary Preferences">
                <Field label="Restrictions"  value={fmtList(np.dietary.restrictions)} muted />
                <Field label="Allergies"     value={np.dietary.allergies_text || '—'} muted />
                <Field label="Cuisines"      value={fmtList(np.dietary.cuisine_prefs)} muted />
                <Field label="Dislikes"      value={np.dietary.disliked_foods || '—'} muted />
                <Field label="Cooking time"  value={fmt(np.dietary.cooking_time)} muted />
                <Field label="Budget"        value={fmt(np.dietary.budget_pref)} muted />
                <Field label="Snacks"        value={np.dietary.include_snacks ? 'Yes' : 'No'} muted />
              </Section>
            )}

            {np.healthShared === false && (
              <Section title="Health">
                <div style={{ padding: '8px 0', fontSize: 13, lineHeight: 1.5, color: c.muted }}>
                  This client hasn't agreed to share health conditions and medication details with their coach,
                  so they aren't shown. They can agree in the app's Settings at any time.
                </div>
              </Section>
            )}

            {np.health?.conditions?.length > 0 && (
              <Section title="Health Conditions">
                <div style={{ padding: '8px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {np.health.conditions.map(cond => (
                    <Badge key={cond} label={cond.replace(/_/g, ' ')} color={c.warning} />
                  ))}
                </div>
              </Section>
            )}

            {np.glp1 && (
              <Section title="GLP-1">
                <Field label="Medication"  value={np.glp1.medication} />
                <Field label="Stage"       value={fmt(np.glp1.dose_stage)} muted />
                <Field label="Time on med" value={fmt(np.glp1.months_on_med)} muted />
                <Field label="Challenges"  value={fmtList(np.glp1.challenges)} muted />
              </Section>
            )}
          </>
        )}

        {/* Danger zone — only shown here for nutrition-only users (no fitness tab) */}
        {isSuperAdmin && !p.fitness_id && (
          <Section title="Danger Zone">
            {!confirmDelete ? (
              <div style={{ padding: '4px 0' }}>
                <ActionBtn label="Delete Account" color={c.error} onClick={() => setConfirmDelete(true)} />
              </div>
            ) : (
              <div style={{ padding: '4px 0' }}>
                <p style={{ color: c.error, fontSize: 13, margin: '0 0 12px', fontWeight: 600 }}>
                  This permanently deletes their account, nutrition data, and all records. Cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <ActionBtn label={acting === 'delete' ? 'Deleting…' : 'Yes, delete permanently'} color={c.error} onClick={() => handleDeleteUser(selected)} disabled={!!acting} />
                  <ActionBtn label="Cancel" color={c.muted} onClick={() => setConfirmDelete(false)} disabled={!!acting} />
                </div>
              </div>
            )}
          </Section>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100vh', background: c.bg, fontFamily: "'Inter', sans-serif", overflow: 'hidden' }}>

      {/* ── Left: List ── */}
      <div style={{ width: selected ? 340 : '100%', minWidth: selected ? 280 : 0, borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', transition: 'width 0.2s', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 16px 12px', borderBottom: `1px solid ${c.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: 13, padding: 0 }}>← Back</button>
            <div style={{ flex: 1 }}>
              <h2 style={{ color: c.text, fontSize: 16, fontWeight: 700, margin: 0 }}>People</h2>
              <p style={{ color: c.muted, fontSize: 12, margin: 0 }}>{loading ? 'Loading…' : `${people.length} total · ${counts.members} active`}</p>
            </div>
            {(isAdmin || isSuperAdmin) && (
              <ActionBtn label="+ Create Coach" color={c.gold} onClick={() => setShowCreateCoach(true)} />
            )}
          </div>

          {/* Search */}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email…"
            style={{
              width: '100%', padding: '8px 12px', background: c.card2,
              border: `1px solid ${c.border}`, borderRadius: 8,
              color: c.text, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8,
            }}
          />

          {/* Lifecycle tabs */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }}>
            {TABS.map(t => {
              const on = filter === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  style={{
                    padding: '6px 11px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap',
                    color: on ? c.bg : c.muted,
                    background: on ? c.gold : c.card2,
                    border: `1px solid ${on ? c.gold : c.border}`,
                    transition: 'all 0.15s',
                  }}
                >
                  {t.label}
                  <span style={{
                    marginLeft: 6, opacity: on ? 0.7 : 0.9,
                    color: on ? c.bg : (t.key === 'needs_action' && t.count > 0 ? c.warning : c.dimmed),
                    fontWeight: 700,
                  }}>{t.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ color: c.muted, textAlign: 'center', padding: '40px 0', fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: c.muted, textAlign: 'center', padding: '40px 0', fontSize: 13 }}>No results</div>
          ) : (
            filtered.map(person => {
              const tm = TYPE_META[person.membership_type] ?? TYPE_META.none;
              const sm = STATUS_META[person.fitness_status];
              const isSelected = selected?.auth_user_id === person.auth_user_id;
              const inactiveReason = getInactiveReason({ ...person, role: person.fitness_role, status: person.fitness_status });
              return (
                <div
                  key={person.auth_user_id}
                  onClick={() => setSelected(isSelected ? null : person)}
                  style={{
                    padding: '11px 16px', cursor: 'pointer',
                    background: isSelected ? `${tm.color}12` : 'transparent',
                    borderLeft: `3px solid ${isSelected ? tm.color : `${tm.color}50`}`,
                    borderBottom: `1px solid ${c.border}`,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: `${tm.color}20`, border: `1px solid ${tm.color}35`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: tm.color, fontSize: 13, fontWeight: 700,
                      }}>
                        {(person.name ?? person.email ?? '?')[0].toUpperCase()}
                      </div>
                      {person.fitness_role === 'client' && (
                        <div style={{
                          position: 'absolute', bottom: 0, right: 0,
                          width: 10, height: 10, borderRadius: '50%',
                          background: activityColor(person.last_workout),
                          border: `2px solid ${isSelected ? `${tm.color}12` : c.bg}`,
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: c.text, fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {person.name ?? <span style={{ color: c.muted, fontStyle: 'italic', fontWeight: 400 }}>No name</span>}
                      </div>
                      <div style={{ color: c.muted, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{person.email}</div>
                      {person.fitness_role === 'client' && (
                        <div style={{ fontSize: 11, marginTop: 1 }}>
                          <span style={{ color: activityColor(person.last_workout) }}>{timeAgo(person.last_workout)}</span>
                          {person.current_week ? <span style={{ color: c.dimmed }}> · Wk {person.current_week}</span> : null}
                          {person.open_flags > 0 ? <span style={{ color: c.warning }}> · {person.open_flags} flag{person.open_flags > 1 ? 's' : ''}</span> : null}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                      <Badge label={tm.label} color={tm.color} />
                      {person.needs_review && <Badge label="New" color={c.warning} />}
                      {person.fitness_role === 'client' && !person.has_program && <Badge label="No program" color={c.error} />}
                      {sm && person.fitness_status !== 'active' && <Badge label={sm.label} color={sm.color} />}
                    </div>
                  </div>

                  {inactiveReason && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, marginTop: 8, paddingLeft: 44,
                    }}>
                      <span style={{ fontSize: 11, color: c.dimmed, fontStyle: 'italic' }}>{inactiveReason}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (person.archived_at) handleRestore(person);
                          else handleArchive(person, 'manual');
                        }}
                        disabled={!!acting}
                        style={{
                          padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                          fontFamily: 'inherit', cursor: acting ? 'not-allowed' : 'pointer',
                          background: 'transparent',
                          border: `1px solid ${person.archived_at ? c.gold : c.border}`,
                          color: person.archived_at ? c.gold : c.muted,
                        }}
                      >
                        {person.archived_at ? 'Restore' : 'Archive'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Right: Detail Panel ── */}
      {selected && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Person header */}
          <div style={{ padding: '20px 24px 0', borderBottom: `1px solid ${c.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                background: `${(TYPE_META[selected.membership_type] ?? TYPE_META.none).color}20`,
                border: `2px solid ${(TYPE_META[selected.membership_type] ?? TYPE_META.none).color}35`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: (TYPE_META[selected.membership_type] ?? TYPE_META.none).color,
                fontSize: 18, fontWeight: 700,
              }}>
                {(selected.name ?? selected.email ?? '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: c.text, fontWeight: 700, fontSize: 16 }}>
                  {selected.name ?? <span style={{ color: c.muted, fontStyle: 'italic' }}>No name</span>}
                </div>
                <div style={{ color: c.muted, fontSize: 13 }}>{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: 20, padding: '0 4px' }}>×</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0 }}>
              {[
                { key: 'fitness',   label: 'Fitness',   show: !!selected.fitness_id },
                { key: 'nutrition', label: 'Nutrition',  show: true },
              ].filter(t => t.show).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    if (tab.key === 'nutrition' && !nutritionProfile && !nutritionLoading) {
                      loadNutritionProfile(selected.auth_user_id);
                    }
                  }}
                  style={{
                    padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                    color: activeTab === tab.key ? c.gold : c.muted,
                    borderBottom: `2px solid ${activeTab === tab.key ? c.gold : 'transparent'}`,
                    transition: 'all 0.15s',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {activeTab === 'fitness' && selected.fitness_id && renderFitnessTab(selected)}
            {activeTab === 'fitness' && !selected.fitness_id && (
              <p style={{ color: c.muted, fontSize: 13 }}>This person has no fitness account — nutrition subscriber only.</p>
            )}
            {activeTab === 'nutrition' && renderNutritionTab(selected)}
          </div>
        </div>
      )}

      {/* Empty state when nothing selected */}
      {!selected && !loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.muted, fontSize: 14 }}>
          Select a person to view details
        </div>
      )}

      {showCreateCoach && (
        <CreateUserModal
          onClose={() => setShowCreateCoach(false)}
          onUserCreated={load}
          coaches={coaches}
          currentUserRole={role}
          currentUserId={currentUser?.auth_user_id}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>{title}</div>
      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: '4px 14px' }}>
        {children}
      </div>
    </div>
  );
}

const selectStyle = {
  width: '100%', padding: '8px 12px', background: '#0F1530',
  border: `1px solid ${theme.surface3}`, borderRadius: 8,
  color: theme.text, fontSize: 13, outline: 'none',
  fontFamily: "'Inter', sans-serif", cursor: 'pointer', colorScheme: 'dark',
};
